import { useState } from "react";
import { Input } from "../forms/Input";
import { Select } from "../forms/Select";
import { Button } from "../Button";
import { parseMarkdown } from "../../utils/markdown";
import { PRODUCT_CATEGORIES } from "../../types";
import type { ProductWithImages } from "../../types";

interface ProductFormProps {
  product?: ProductWithImages;
  onSubmit: (data: any) => Promise<void>;
  onCancel: () => void;
}

export function ProductForm({ product, onSubmit, onCancel }: ProductFormProps) {
  const [formData, setFormData] = useState({
    name: product?.name || "",
    description: product?.description || "",
    price: product?.price || "",
    category: product?.category || "",
    inventory: product?.inventory || "",
    featured: product?.featured || false,
  });
  const [images, setImages] = useState<string[]>(
    product?.images?.map((img) => img.image_path) || []
  );
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);

  // Drag-and-drop state
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox" ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    try {
      const uploadedPaths: string[] = [];

      for (const file of Array.from(files)) {
        const formData = new FormData();
        formData.append("image", file);

        const response = await fetch("/api/admin/upload", {
          method: "POST",
          body: formData,
        });

        if (!response.ok) throw new Error("Upload failed");

        const data = await response.json();
        uploadedPaths.push(data.path);
      }

      setImages((prev) => [...prev, ...uploadedPaths]);
    } catch (error) {
      console.error("Error uploading images:", error);
      alert("Failed to upload images");
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (index: number) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  // Drag-and-drop handlers for image reordering
  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== index) {
      const newImages = [...images];
      const [removed] = newImages.splice(draggedIndex, 1);
      newImages.splice(index, 0, removed);
      setImages(newImages);
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const productData = {
        ...formData,
        images: images.map((path, index) => ({
          path,
          isPrimary: index === 0,
          displayOrder: index,
        })),
      };

      await onSubmit(productData);
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("Failed to save product");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Input
        label="Product Name"
        name="name"
        value={formData.name}
        onChange={handleChange}
        required
      />

      <div>
        <label className="block text-sm font-medium mb-2">Description</label>
        <div className="text-xs text-gray-500 mb-2 space-y-1">
          <p>Markdown formatting supported:</p>
          <div className="flex flex-wrap gap-3">
            <span><code className="bg-gray-100 px-1 rounded">**bold**</code></span>
            <span><code className="bg-gray-100 px-1 rounded">*italic*</code></span>
            <span><code className="bg-gray-100 px-1 rounded">__underline__</code></span>
            <span><code className="bg-gray-100 px-1 rounded">[link](url)</code></span>
            <span><code className="bg-gray-100 px-1 rounded">- list item</code></span>
          </div>
        </div>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleChange}
          className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-black font-mono text-sm"
          rows={8}
          placeholder="Enter product description with Markdown formatting..."
        />
        <div className="text-xs text-gray-500 mt-1">
          {(formData.description || "").length} / 10,000 characters
        </div>

        {formData.description && (
          <div className="mt-4">
            <h4 className="text-sm font-medium mb-2">Preview:</h4>
            <div
              className="prose prose-sm border border-gray-200 rounded p-4 bg-gray-50 max-h-64 overflow-y-auto"
              dangerouslySetInnerHTML={{ __html: parseMarkdown(formData.description) }}
            />
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Input
          label="Price"
          name="price"
          type="number"
          step="0.01"
          value={formData.price}
          onChange={handleChange}
          required
        />

        <Input
          label="Inventory"
          name="inventory"
          type="number"
          value={formData.inventory}
          onChange={handleChange}
          required
        />
      </div>

      <Select
        label="Category"
        name="category"
        value={formData.category}
        onChange={handleChange}
        required
      >
        <option value="">Select a category</option>
        {PRODUCT_CATEGORIES.map((cat) => (
          <option key={cat.value} value={cat.value}>
            {cat.label}
          </option>
        ))}
      </Select>

      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="featured"
          name="featured"
          checked={formData.featured}
          onChange={handleChange}
          className="w-4 h-4"
        />
        <label htmlFor="featured" className="text-sm font-medium">
          Featured Product
        </label>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">Product Images</label>
        <input
          type="file"
          accept="image/*"
          multiple
          onChange={handleImageUpload}
          className="w-full px-4 py-2 border border-gray-300 rounded"
          disabled={uploading}
        />
        {uploading && <p className="text-sm text-gray-600 mt-1">Uploading...</p>}
      </div>

      {images.length > 0 && (
        <>
          <div className="grid grid-cols-4 gap-3">
            {images.map((path, index) => (
              <div
                key={`${path}-${index}`}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, index)}
                onDragEnd={handleDragEnd}
                className={`relative group cursor-grab active:cursor-grabbing transition-all duration-200
                  ${draggedIndex === index ? "opacity-50 scale-95" : ""}
                  ${dragOverIndex === index ? "ring-2 ring-blue-500 ring-offset-2" : ""}`}
              >
                <img
                  src={path}
                  alt={`Product ${index + 1}`}
                  className="w-full aspect-square object-cover rounded pointer-events-none"
                />
                {index === 0 && (
                  <span className="absolute top-1 left-1 bg-black text-white text-xs px-2 py-1 rounded">
                    Primary
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => removeImage(index)}
                  className="absolute top-1 right-1 bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-sm font-bold"
                >
                  ×
                </button>
                <div className="absolute bottom-1 left-1/2 -translate-x-1/2 bg-black/60 text-white text-xs px-2 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                  Drag to reorder
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Drag images to reorder. The first image will be used as the primary display image.
          </p>
        </>
      )}

      <div className="flex gap-2 pt-4">
        <Button type="submit" variant="primary" loading={loading} className="flex-1">
          {product ? "Update Product" : "Create Product"}
        </Button>
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
